import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

export interface GeneralNotification {
  id: string;
  type: 'quote' | 'carrier' | 'contact' | 'sla' | 'payment' | 'document' | 'quote_available' | 'quote_status_change' | 'chat';
  title: string;
  message: string;
  link: string;
  created_at: string;
  priority: 'high' | 'medium' | 'low';
  data?: any;
}

export const useGeneralNotifications = () => {
  return useQuery({
    queryKey: ['general-notifications'],
    queryFn: async () => {
      const notifications: GeneralNotification[] = [];

      // Get current user role to filter notifications appropriately
      const { data: { user } } = await supabase.auth.getUser();
      let userRole: 'admin' | 'agent' | 'user' | null = null;
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        userRole = profile?.role || null;
      }

      // Only fetch admin/agent notifications if user is admin or agent
      const isAdminOrAgent = userRole === 'admin' || userRole === 'agent';

      // 0. Get notifications from notifications table (for admin/agent)
      if (isAdminOrAgent) {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const { data: dbNotifications, error: dbNotificationsError } = await supabase
          .from('notifications')
          .select('*')
          .gte('created_at', sevenDaysAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(50);

        if (!dbNotificationsError && dbNotifications) {
          logger.log('📬 Fetched notifications from DB:', {
            count: dbNotifications.length,
            types: Array.from(new Set(dbNotifications.map(n => n.type))),
          });

          dbNotifications.forEach((notif) => {
            // Include ALL relevant notifications for admin/agent
            // Filter out client-specific notifications (those without quote_id or carrier_id in metadata)
            const quoteId = notif.metadata?.quote_id;
            const carrierId = notif.metadata?.carrier_id;
            const status = notif.metadata?.status || '';
            
            // Include notifications that are relevant to admin/agent:
            // - quote_status_change (all status changes)
            // - carrier_assigned / carrier_unassigned
            // - carrier (carrier accepted quote, etc.)
            // - payment (if relevant to admin)
            // - document (if relevant to admin)
            // - contact (if relevant to admin)
            // NOTE: chat notifications are NOT included - they are handled by the floating chat button
            const isRelevant = 
              notif.type === 'quote_status_change' ||
              notif.type === 'carrier_assigned' ||
              notif.type === 'carrier_unassigned' ||
              notif.type === 'carrier' ||
              (notif.type === 'payment' && quoteId) ||
              (notif.type === 'document' && carrierId) ||
              (notif.type === 'contact');
              // (notif.type === 'chat'); // EXCLUDED - handled by floating chat button
            
            if (isRelevant) {
              // Determine priority based on type and status
              let priority: 'high' | 'medium' | 'low' = 'medium';
              if (notif.type === 'quote_status_change') {
                priority = status === 'ready_for_payment' ? 'high' : 
                          status === 'paid' ? 'high' : 
                          status === 'confirmed' ? 'high' :
                          'medium';
              } else if (notif.type === 'carrier_assigned' || notif.type === 'carrier') {
                priority = 'high';
              } else if (notif.type === 'payment') {
                priority = 'high';
              } else if (notif.type === 'contact') {
                priority = 'medium';
              }
              
              // Build link with quote_id or carrier_id if available
              let notificationLink = notif.link || '/admin/quotes';
              if (quoteId && !notificationLink.includes('quote=')) {
                notificationLink = `/admin/quotes?quote=${quoteId}`;
              } else if (carrierId && !notificationLink.includes('carrier=')) {
                notificationLink = `/admin/carriers?carrier=${carrierId}`;
              }
              
              // Map notification types to GeneralNotification types
              let mappedType: GeneralNotification['type'] = 'quote';
              if (notif.type === 'quote_status_change') {
                mappedType = 'quote_status_change';
              } else if (notif.type === 'carrier' || notif.type === 'carrier_assigned' || notif.type === 'carrier_unassigned') {
                mappedType = 'quote'; // Show as quote-related
              } else if (notif.type === 'payment') {
                mappedType = 'payment';
              } else if (notif.type === 'document') {
                mappedType = 'document';
              } else if (notif.type === 'contact') {
                mappedType = 'contact';
              }
              // Chat notifications are excluded - handled by floating chat button
              
              notifications.push({
                id: notif.id,
                type: mappedType,
                title: notif.title || 'Notification',
                message: notif.message || 'New notification',
                link: notificationLink,
                created_at: notif.created_at,
                priority: priority,
                data: {
                  quote_id: quoteId,
                  carrier_id: carrierId,
                  status: status,
                  ...notif.metadata
                },
              });
            }
          });
        } else if (dbNotificationsError) {
          logger.error('Error fetching notifications from DB:', dbNotificationsError);
        }
      }

      // 1. Obtener quotes pendientes
      const { data: pendingQuotes, error: quotesError } = await supabase
        .from('quotes')
        .select('id, name, email, status, created_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(10);

      if (!quotesError && pendingQuotes) {
        pendingQuotes.forEach((quote) => {
          notifications.push({
            id: `quote-${quote.id}`,
            type: 'quote',
            title: 'New Quote Request',
            message: `${quote.name} - ${quote.email}`,
            link: `/admin/quotes?quote=${quote.id}`,
            created_at: quote.created_at,
            priority: 'high',
            data: quote,
          });
        });
      }

      // 2. Obtener carriers pendientes (no conformes o con revisión pendiente)
      const { data: pendingCarriers, error: carriersError } = await supabase
        .from('carrier_registrations')
        .select('id, company_name, email, compliance_status, created_at')
        .neq('compliance_status', 'compliant')
        .order('created_at', { ascending: false })
        .limit(10);

      if (!carriersError && pendingCarriers) {
        pendingCarriers.forEach((carrier) => {
          notifications.push({
            id: `carrier-${carrier.id}`,
            type: 'carrier',
            title: 'New Carrier Registration',
            message: `${carrier.company_name} - ${carrier.email}`,
            link: `/admin/carriers?carrier=${carrier.id}`,
            created_at: carrier.created_at,
            priority: 'medium',
            data: carrier,
          });
        });
      }

      // 3. Obtener mensajes de contacto pendientes
      const { data: pendingMessages, error: messagesError } = await supabase
        .from('messages')
        .select('id, name, email, subject, status, created_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(10);

      if (!messagesError && pendingMessages) {
        pendingMessages.forEach((message) => {
          notifications.push({
            id: `message-${message.id}`,
            type: 'contact',
            title: 'New Contact Message',
            message: `${message.name} - ${message.subject || 'No subject'}`,
            link: `/admin/messages?message=${message.id}`,
            created_at: message.created_at,
            priority: 'medium',
            data: message,
          });
        });
      }

      // 4. Obtener quotes pagados recientemente (últimas 24 horas)
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      
      const { data: paidQuotes, error: paidQuotesError } = await supabase
        .from('quotes')
        .select('id, name, email, status, updated_at')
        .eq('status', 'paid')
        .gte('updated_at', oneDayAgo.toISOString())
        .order('updated_at', { ascending: false })
        .limit(10);

      if (!paidQuotesError && paidQuotes) {
        paidQuotes.forEach((quote) => {
          notifications.push({
            id: `payment-${quote.id}`,
            type: 'payment',
            title: 'Payment Received',
            message: `${quote.name} completed payment`,
            link: `/admin/quotes?quote=${quote.id}`,
            created_at: quote.updated_at,
            priority: 'high',
            data: quote,
          });
        });
      }

      // 5. Obtener documentos subidos por carriers (no verificados, últimas 48 horas)
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      
      const { data: newDocuments, error: documentsError } = await supabase
        .from('carrier_documents')
        .select(`
          id,
          file_name,
          document_type,
          uploaded_at,
          verified,
          carrier_id,
          carrier_registrations!inner(company_name, email)
        `)
        .eq('verified', false)
        .gte('uploaded_at', twoDaysAgo.toISOString())
        .order('uploaded_at', { ascending: false })
        .limit(15);

      if (!documentsError && newDocuments) {
        newDocuments.forEach((doc: any) => {
          const carrierName = doc.carrier_registrations?.company_name || 'Unknown Carrier';
          notifications.push({
            id: `document-${doc.id}`,
            type: 'document',
            title: 'New Document Uploaded',
            message: `${carrierName} uploaded ${doc.document_type.replace('_', ' ')}`,
            link: `/admin/carriers?carrier=${doc.carrier_id}`,
            created_at: doc.uploaded_at,
            priority: 'medium',
            data: doc,
          });
        });
      }

      // Deduplicate by id to avoid repeated notifications when multiple sources overlap.
      const deduped = Array.from(
        notifications.reduce((map, notification) => {
          map.set(notification.id, notification);
          return map;
        }, new Map<string, GeneralNotification>()).values()
      );

      // Ordenar por fecha de creación (más recientes primero)
      return deduped.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    refetchInterval: 60000, // Refrescar cada minuto
    staleTime: 30000, // Considerar datos frescos por 30 segundos
  });
};

export const useNotificationCount = () => {
  return useQuery({
    queryKey: ['notification-count'],
    queryFn: async () => {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      const [quotesResult, carriersResult, messagesResult, paymentsResult, documentsResult] = await Promise.all([
        supabase
          .from('quotes')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('carrier_registrations')
          .select('id', { count: 'exact', head: true })
          .neq('compliance_status', 'compliant'),
        supabase
          .from('messages')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('quotes')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'paid')
          .gte('updated_at', oneDayAgo.toISOString()),
        supabase
          .from('carrier_documents')
          .select('id', { count: 'exact', head: true })
          .eq('verified', false)
          .gte('uploaded_at', twoDaysAgo.toISOString()),
      ]);

      const quoteCount = quotesResult.count || 0;
      const carrierCount = carriersResult.count || 0;
      const messageCount = messagesResult.count || 0;
      const paymentCount = paymentsResult.count || 0;
      const documentCount = documentsResult.count || 0;

      return {
        total: quoteCount + carrierCount + messageCount + paymentCount + documentCount,
        quotes: quoteCount,
        carriers: carrierCount,
        messages: messageCount,
        payments: paymentCount,
        documents: documentCount,
      };
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });
};

